import { useEffect, useState } from "react";
import type { PivotControlsSettings, SceneNodeInfo } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { CompactCheck, CompactSlider, MiniBtn, Row } from "../../inspector/rows";

function findNodeByGroupId(nodes: SceneNodeInfo[], groupId: string): SceneNodeInfo | null {
  for (const n of nodes) {
    if (n.groupId === groupId) return n;
    const found = findNodeByGroupId(n.children, groupId);
    if (found) return found;
  }
  return null;
}

/** Pivot Controls + Pivot Edit (v19, iJewel). Stored settings round-trip; the
 * pivot-to-* actions reuse setPivot on the current selection. `setPivot` only
 * resolves scene-tree "node_N" ids, but `getSelection()` returns material-group
 * ids — so the current selection is mapped through `getSceneTree()` first. */
export function PivotControlsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [p, setP] = useState<PivotControlsSettings | null>(null);
  useEffect(() => { r.getPivotControls().then(setP).catch(() => {}); }, [r]);
  const sp = (patch: Partial<PivotControlsSettings>) => r.setPivotControls(patch).then(setP);
  const pivot = (mode: "center" | "bottom" | "origin") =>
    r.getSelection().then(async (ids) => {
      const groupId = ids[0];
      if (!groupId) return;
      const tree = await r.getSceneTree();
      const node = findNodeByGroupId(tree, groupId);
      if (node) void r.setPivot(node.id, mode);
    });
  return (
    <div className="flex flex-col">
      {p ? (
        <>
          <Row label="Enabled"><CompactCheck checked={p.enabled} onChange={(v) => void sp({ enabled: v })} /></Row>
          <Row label="Gizmo Scale"><CompactSlider value={p.gizmoScale} min={0.1} max={10} step={0.01} onChange={(v) => void sp({ gizmoScale: v })} /></Row>
          <Row label="Fixed Size"><CompactCheck checked={p.fixed} onChange={(v) => void sp({ fixed: v })} /></Row>
          <Row label="Depth Test"><CompactCheck checked={p.depthTest} onChange={(v) => void sp({ depthTest: v })} /></Row>
          <Row label="Translation Snap"><CompactSlider value={p.translationSnap} min={0.01} max={5} step={0.01} onChange={(v) => void sp({ translationSnap: v })} /></Row>
          <Row label="Rotation Snap (deg)"><CompactSlider value={p.rotationSnap} min={1} max={90} step={1} onChange={(v) => void sp({ rotationSnap: v })} /></Row>
          <Row label="Scale Snap"><CompactSlider value={p.scaleSnap} min={0.01} max={1} step={0.01} onChange={(v) => void sp({ scaleSnap: v })} /></Row>
          <Row label="Disable Axes"><CompactCheck checked={p.disableAxes} onChange={(v) => void sp({ disableAxes: v })} /></Row>
          <Row label="Disable Sliders"><CompactCheck checked={p.disableSliders} onChange={(v) => void sp({ disableSliders: v })} /></Row>
          <Row label="Disable Rotations"><CompactCheck checked={p.disableRotations} onChange={(v) => void sp({ disableRotations: v })} /></Row>
          <Row label="Disable Scaling"><CompactCheck checked={p.disableScaling} onChange={(v) => void sp({ disableScaling: v })} /></Row>
        </>
      ) : null}
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn onClick={() => void pivot("center")}>Pivot to Center</MiniBtn>
        <MiniBtn onClick={() => void pivot("bottom")}>Pivot to Bottom</MiniBtn>
        <MiniBtn onClick={() => void pivot("origin")}>Pivot to Origin</MiniBtn>
      </div>
    </div>
  );
}
